import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Link,
} from '@mui/material';
import {
  PlayArrow as ProcessIcon,
  History as HistoryIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import axios from '../api';

interface PayrollRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  grossPay: number;
  deductions: number;
  netPay: number;
  status: 'pending' | 'processed' | 'paid';
  processedDate: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  salary: number;
}

interface PayrollFileFormatState {
  selectedFile: File | null;
  uploading: boolean;
  uploadSuccessMessage: string;
  downloadUrl: string;
  error: string;
}

const Payroll: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [payrollRecords, setPayrollRecords] = useState<PayrollRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [payPeriod, setPayPeriod] = useState({
    start: '',
    end: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [payrollFileFormatState, setPayrollFileFormatState] = useState<PayrollFileFormatState>({
    selectedFile: null,
    uploading: false,
    uploadSuccessMessage: '',
    downloadUrl: '',
    error: '',
  });

  useEffect(() => {
    if (tabValue === 0 || tabValue === 1) {
    fetchPayrollRecords();
    fetchEmployees();
    }
  }, [tabValue]);

  const fetchPayrollRecords = async () => {
    try {
      const response = await axios.get('/api/payroll');
      setPayrollRecords(response.data);
    } catch (error) {
      console.error('Error fetching payroll records:', error);
      showSnackbar('Error fetching payroll records', 'error');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/api/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showSnackbar('Error fetching employees', 'error');
    }
  };

  const handleProcessPayroll = async () => {
    if (!selectedEmployee || !payPeriod.start || !payPeriod.end) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/payroll/process', {
        employeeId: selectedEmployee,
        payPeriodStart: payPeriod.start,
        payPeriodEnd: payPeriod.end,
      });
      showSnackbar('Payroll processed successfully');
      fetchPayrollRecords();
      handleProcessDialogClose();
    } catch (error) {
      console.error('Error processing payroll:', error);
      showSnackbar('Error processing payroll', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDialogClose = () => {
    setProcessDialogOpen(false);
    setSelectedEmployee('');
    setPayPeriod({ start: '', end: '' });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue !== 2) {
      setPayrollFileFormatState({
        selectedFile: null,
        uploading: false,
        uploadSuccessMessage: '',
        downloadUrl: '',
        error: '',
      });
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setPayrollFileFormatState(prev => ({
        ...prev,
        selectedFile: event.target.files![0],
        uploadSuccessMessage: '',
        downloadUrl: '',
        error: '',
      }));
    }
  };

  const handleFileUpload = async () => {
    if (!payrollFileFormatState.selectedFile) {
      setPayrollFileFormatState(prev => ({ ...prev, error: 'Please select a file to upload.' }));
      return;
    }

    setPayrollFileFormatState(prev => ({ ...prev, uploading: true, error: '', uploadSuccessMessage: '', downloadUrl: '' }));

    const formData = new FormData();
    formData.append('payrollExport', payrollFileFormatState.selectedFile);

    try {
      const response = await axios.post('/api/upload-payroll', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPayrollFileFormatState(prev => ({
        ...prev,
        uploading: false,
        uploadSuccessMessage: response.data.message || 'File processed successfully!',
        downloadUrl: response.data.downloadUrl,
      }));
      showSnackbar(response.data.message || 'File processed successfully!', 'success');
    } catch (err: any) {
      console.error('Error uploading file:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Failed to upload and process file.';
      setPayrollFileFormatState(prev => ({
        ...prev,
        uploading: false,
        error: errorMessage,
      }));
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payroll</Typography>
        {tabValue !== 2 && (
        <Button
          variant="contained"
          startIcon={<ProcessIcon />}
          onClick={() => setProcessDialogOpen(true)}
        >
          Process Payroll
        </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="payroll tabs">
          <Tab label="Current Pay Period" />
          <Tab label="Payroll History" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="File Formatter" icon={<UploadFileIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {(tabValue === 0 || tabValue === 1) && (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Pay Period</TableCell>
              <TableCell>Gross Pay</TableCell>
              <TableCell>Deductions</TableCell>
              <TableCell>Net Pay</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Processed Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrollRecords
              .filter(record => tabValue === 0 ? record.status === 'pending' : true)
              .map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.employeeName}</TableCell>
                  <TableCell>{`${new Date(record.payPeriodStart).toLocaleDateString()} - ${new Date(record.payPeriodEnd).toLocaleDateString()}`}</TableCell>
                  <TableCell>${record.grossPay.toLocaleString()}</TableCell>
                  <TableCell>${record.deductions.toLocaleString()}</TableCell>
                  <TableCell>${record.netPay.toLocaleString()}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{record.status}</TableCell>
                  <TableCell>{record.processedDate ? new Date(record.processedDate).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Payroll File Formatter
          </Typography>
          <Typography variant="body1" paragraph>
            Upload your <code>payroll_export.xlsx</code> file. The system will process it using the
            stored formatter template and provide a download link for the formatted Excel file.
          </Typography>
          
          <Box component="form" noValidate autoComplete="off" sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={payrollFileFormatState.uploading}
            >
              {payrollFileFormatState.selectedFile ? payrollFileFormatState.selectedFile.name : "Select payroll_export.xlsx"}
              <input
                type="file"
                hidden
                accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {payrollFileFormatState.selectedFile && !payrollFileFormatState.uploading && (
            <Typography variant="body2" color="text.secondary" sx={{mb:2}}>
                Selected: {payrollFileFormatState.selectedFile.name}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleFileUpload}
            disabled={!payrollFileFormatState.selectedFile || payrollFileFormatState.uploading}
            startIcon={payrollFileFormatState.uploading ? <CircularProgress size={20} color="inherit" /> : <ProcessIcon />}
            sx={{mb: 2}}
          >
            {payrollFileFormatState.uploading ? 'Processing...' : 'Upload and Process File'}
          </Button>

          {payrollFileFormatState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>{payrollFileFormatState.error}</Alert>
          )}
          {payrollFileFormatState.uploadSuccessMessage && payrollFileFormatState.downloadUrl && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {payrollFileFormatState.uploadSuccessMessage}
              <br />
              <Button
                variant="outlined"
                color="primary"
                onClick={async () => {
                  try {
                    const downloadUrl = payrollFileFormatState.downloadUrl;
                    console.log('Download URL:', downloadUrl);
                    
                    const response = await axios.get(downloadUrl, {
                      responseType: 'blob',
                    });

                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', downloadUrl.split('/').pop() || 'formatted_output.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    console.log('Download initiated successfully');
                    showSnackbar('File downloaded successfully', 'success');
                  } catch (error) {
                    console.error('Error downloading file:', error);
                    showSnackbar('Error downloading file. Please try again.', 'error');
                  }
                }}
              >
                Download Formatted File
              </Button>
            </Alert>
          )}
        </Paper>
      )}

      <Dialog open={processDialogOpen} onClose={handleProcessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Process Payroll</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={selectedEmployee}
                label="Employee"
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.firstName} ${employee.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Pay Period Start"
              type="date"
              value={payPeriod.start}
              onChange={(e) => setPayPeriod({ ...payPeriod, start: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Pay Period End"
              type="date"
              value={payPeriod.end}
              onChange={(e) => setPayPeriod({ ...payPeriod, end: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProcessDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleProcessPayroll}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Process
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payroll; 